import { nextTick } from "vue";

import { stickyNoteCreated } from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";
import { writer } from "@/backend/write/BackendWriter";
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- context menu component, not a modal
import CardCreationMenu from "@/components/menu/CardCreationMenu.vue";
import { sendStickyNoteAction } from "@/composables/useEventBus";
import { asLen, windowFromRelative } from "@/math/coordinate-transform";
import { isOffScreen, relativeFromViewport } from "@/math/coordinate-utils";
import { divided, insideRectangle, plus, rectangle, times } from "@/math/coordinates";
import type { AlmSourceId } from "@/model/baseTypes";
import { assureBoardType, isBacklogBoard } from "@/model/board";
import type { BoardCard } from "@/model/card";
import type { ViewportCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import type { StickyType } from "@/model/stickyType";
import { useAlmItemTypeStore } from "@/store/almItemType";
import { cardSize, useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import type { CardEvent } from "@/store/card";
import { useCardStore } from "@/store/card";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useContextMenuStore } from "@/store/contextMenu";
import { linkHighlightCard } from "@/store/helpers/mark";
import { useLinkStore } from "@/store/link";
import { isStickyTypeAlmSynced } from "@/store/stickyType";
import { useUserStore } from "@/store/user";
import { boundsRect, centerCoord, viewportFromWindow } from "@/utils/dom/coordinates";
import { mousePointer } from "@/utils/dom/pointer";
import { findFreePosition } from "@/utils/layout";
import { removeNonPrintable } from "@/utils/string";
import { withHardBreaks } from "@/utils/text/lineBreaker";
import { hardBreaksAllowed, maxStickyTextLength } from "@/utils/text/sticky";
import type { Replace, UnknownRecord } from "@/utils/type";

import { internalActions } from "./internalActions";
import { linkActions } from "./linkActions";
import { toggleActions } from "./toggleActions";
import type { ActionContext } from "./types";

// CardCreateProps types are stepwise more specific:
// StartCardCreateProps -> resolve pos -> PosCardCreateProps -> resolve type -> CardCreateProps
interface StartCardCreateProps {
  type?: StickyType;
  pos: StartCardPos;
  text?: string;
  almSourceId?: AlmSourceId;
  almId?: string;
  onClose?: () => void;
}

export type PosCardCreateProps = Replace<StartCardCreateProps, { pos: ViewportCoordinate }>;

export type CardCreateProps = Replace<PosCardCreateProps, { type: StickyType }>;

type StartCardPos = ViewportCoordinate | "auto" | "pointer";

function isCardCreateProps(props: PosCardCreateProps): props is CardCreateProps {
  return props.type !== undefined;
}

export async function startAddCard(ctx: ActionContext, props: StartCardCreateProps, focus = true) {
  if (!useUserStore().isAllowed("edit")) return;
  const pos = resolvePos(props.pos);
  const type = props.type ?? getDefaultStickyNoteType();
  return posAddCard(ctx, { ...props, pos, type }, focus);
}

async function posAddCard(ctx: ActionContext, props: PosCardCreateProps, focus: boolean) {
  if (isCardCreateProps(props) && isAlmSourceOk(props) && !props.text?.includes("\n")) {
    return addAndTrack(props.type, focus, {});
  }

  useContextMenuStore().open(
    CardCreationMenu,
    { position: props.pos, card: props },
    { select, close: () => props.onClose?.() },
  );

  async function select(
    type: StickyType,
    almSourceId: AlmSourceId | undefined,
    multiCreate: boolean,
  ) {
    if (multiCreate) {
      const addPromises = props
        .text!.split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((text, index) => {
          const offset = asLen(windowFromRelative(times(cardPosStep(), index)));
          const pos = plus(props.pos, offset);
          return addAndTrack(type, false, { almSourceId, text, pos });
        });
      const boardId = useBoardStore().currentBoard().id;
      for (const id of await Promise.all(addPromises)) {
        internalActions.cardToFront(id, boardId);
        useBoardStore().selectSticky(id);
      }
    } else {
      void addAndTrack(type, focus, { almSourceId });
    }
  }

  function addAndTrack(type: StickyType, focus: boolean, additional: UnknownRecord) {
    trackEvent(
      stickyNoteCreated(type.functionality, type.origin, ctx.source, !!getDefaultStickyNoteType()),
    );
    const text = adjustText(props.text, type);
    return addCard({ ...props, type, text, ...additional }, focus);
  }
}

function adjustText(text: string | undefined, type: StickyType) {
  return text
    ? withHardBreaks(text, hardBreaksAllowed(type)).slice(0, maxStickyTextLength())
    : text;
}

function getDefaultStickyNoteType() {
  const id = useClientSettingsStore().defaultStickyTypeId;
  return useBoardStore().creatableStickyTypes.find((type) => type.id === id);
}

const boardRect = rectangle({ from: relativeCoord(0, 0), to: relativeCoord(1, 1) });

function resolvePos(pos: StartCardPos): ViewportCoordinate {
  switch (pos) {
    case "auto": {
      const activeElement = document.activeElement;
      // If the user is focused on a board element, create the new sticky at that location
      if (activeElement?.closest(".board")) {
        const rect = boundsRect(activeElement);
        const f = activeElement.classList.contains("sticky-note") ? 1.5 : 2; // Avoid creating a sticky note on top of another one
        return plus(rect.p0, divided(rect.size, f));
      }
      // Otherwise, create the new sticky in the center of the board
      return centerCoord();
    }
    case "pointer":
      return isOffScreen(mousePointer) ||
        !insideRectangle(relativeFromViewport(mousePointer), boardRect)
        ? centerCoord()
        : mousePointer;
    default:
      return pos;
  }
}

function isAlmSourceOk(props: CardCreateProps) {
  const originBoard = useBoardsStore().stickyTypeOriginBoard(props.type, props.pos);
  return props.almSourceId !== undefined || originBoard.almSources.length <= 1;
}

export async function addCard(props: CardCreateProps, focus = true) {
  toggleActions.hidePanMode("internal");
  const board = useBoardStore().currentBoard();
  props = sanitize(props);
  const originBoard = useBoardsStore().stickyTypeOriginBoard(props.type, props.pos);
  const almSourceId = props.almSourceId ?? originBoard.almSources[0]?.id;
  const status = useAlmItemTypeStore().calcStatus(
    undefined,
    props.type,
    assureBoardType(board, "team")?.team.id,
    board.artId,
    almSourceId,
  );
  const pos = relativeFromViewport(props.pos);
  const ev: CardEvent = {
    id: "",
    boardId: board.id,
    pos,
    text: props.text,
    type: props.type,
    status,
    priority: 0,
    iterationId: null,
    teamId: null,
    zIndex: 0,
    flagType: null,
    objectives: [],
    almSourceId,
    almId: props.almId,
    artId: board.artId || null,
    reporter: useUserStore().currentUser,
    ...useBoardStore().positionalCardProperties(pos),
  };
  // a backlog sticky has neither a position nor a stacking order
  const { pos: cardPos, zIndex, ...card } = ev;
  ev.id = await writer.addCard(board.id, {
    ...card,
    ...(isBacklogBoard(board.type) ? {} : { ...cardPos, zIndex }),
  });
  useCardStore().add(ev, board);
  // Make sure the new card matches the current faded state (eg. when a card is pinned)
  internalActions.updateCardLinkedMarks();
  const pinnedCards = useLinkStore().markingCardsLinkedCards;
  // Only auto-link when exactly one card is pinned to avoid ambiguity
  if (pinnedCards?.length === 1) {
    const pinnedCard = pinnedCards[0];
    void linkActions.add("pin-create", { fromId: pinnedCard.data.id, toId: ev.id });
    linkHighlightCard(board.cards[ev.id], true);
    internalActions.cardToFront(ev.id);
  }

  if (focus) {
    await nextTick();
    sendStickyNoteAction(ev.id, { action: "enlarge", focus: true, trigger: "after-create" });
  }
  return ev.id;
}

function sanitize(event: CardCreateProps): CardCreateProps {
  if (event.text && isStickyTypeAlmSynced(event.type)) {
    event.text = removeNonPrintable(event.text);
  }
  return event;
}

export function cardCreateProps(
  card: BoardCard,
  options?: { findFreePos?: boolean },
): CardCreateProps {
  return {
    text: card.data.text,
    type: card.data.type,
    pos: viewportFromWindow(
      windowFromRelative(
        options?.findFreePos
          ? findFreePosition(card.meta.pos, cardPosStep())
          : plus(card.meta.pos, cardPosStep()),
      ),
    ),
    almSourceId: card.data.almSourceId ?? undefined,
  };
}

export function cardPosStep() {
  return times(cardSize(), 0.4);
}
